import { createFileRoute } from '@tanstack/react-router'
import { useState, useRef, useEffect } from 'react'
import { useQuery as useConvexQuery } from 'convex/react'
import { api } from 'convex/_generated/api'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import type { Id } from 'convex/_generated/dataModel'
import { Volume2, Play, Pause, Save, RotateCcw } from 'lucide-react'
import { useNativeAudioPlayer } from '@/hooks/useNativeAudioPlayer'

export const Route = createFileRoute('/app/edit-all')({
  component: EditTimestampsPage,
})

function EditTimestampsPage() {
  const currentUser = useCurrentUser()

  if (currentUser === undefined) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-sky-blue text-xl font-mono">Loading...</div>
      </div>
    )
  }

  if (currentUser === null) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-trail-tan text-xl font-mono">Please sign in to edit timestamps</div>
      </div>
    )
  }

  return <EditTimestampsContent userId={currentUser._id} />
}

function EditTimestampsContent({ userId }: { userId: Id<'users'> }) {
  // Load all flashcards with source songs
  const flashcards = useConvexQuery(api.flashcards.getAllFlashcardsWithAudio, {
    userId,
  })

  const [editedTimestamps, setEditedTimestamps] = useState<Map<Id<'flashcards'>, { start: number; end: number }>>(new Map())
  const [selectedCard, setSelectedCard] = useState<Id<'flashcards'> | null>(null)
  const audioRef = useRef<HTMLAudioElement>(null)
  const [localCurrentTime, setLocalCurrentTime] = useState(0)
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0)

  // Get currently selected flashcard data
  const currentCard = flashcards?.find(card => card._id === selectedCard)

  // Use unified audio player hook for coordination
  const { play, pause, seek, isPlaying, currentTime } = useNativeAudioPlayer({
    audioRef,
    audioId: currentCard?._id || 'edit-timestamps',
    audioUrl: currentCard?.audioUrl || '',
  })

  // Sync local current time with hook's current time
  useEffect(() => {
    setLocalCurrentTime(currentTime)
  }, [currentTime])

  const formatTimeMs = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    const ms = Math.floor((seconds % 1) * 1000)
    return `${mins}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`
  }

  const parseTimeMs = (timeStr: string): number => {
    const parts = timeStr.split(':')
    if (parts.length !== 2) return 0

    const [mins, secMs] = parts
    const [secs, ms = '0'] = secMs.split('.')

    return parseInt(mins) * 60 + parseInt(secs) + parseInt(ms) / 1000
  }

  const handleStartTimeChange = (cardId: Id<'flashcards'>, value: string) => {
    const time = parseTimeMs(value)
    const current = editedTimestamps.get(cardId) || { start: 0, end: 0 }
    const newMap = new Map(editedTimestamps)
    newMap.set(cardId, { ...current, start: time })
    setEditedTimestamps(newMap)
  }

  const handleEndTimeChange = (cardId: Id<'flashcards'>, value: string) => {
    const time = parseTimeMs(value)
    const current = editedTimestamps.get(cardId) || { start: 0, end: 0 }
    const newMap = new Map(editedTimestamps)
    newMap.set(cardId, { ...current, end: time })
    setEditedTimestamps(newMap)
  }

  const captureCurrentTime = (field: 'start' | 'end') => {
    if (!audioRef.current || !selectedCard) return

    const time = audioRef.current.currentTime
    const current = editedTimestamps.get(selectedCard) || { start: 0, end: 0 }
    const newMap = new Map(editedTimestamps)
    newMap.set(selectedCard, { ...current, [field]: time })
    setEditedTimestamps(newMap)
  }

  const playSegment = (start: number, end: number) => {
    if (!audioRef.current) return

    seek(start)
    play()

    // Set up automatic pause at end time
    const checkEnd = setInterval(() => {
      if (audioRef.current && audioRef.current.currentTime >= end) {
        pause()
        clearInterval(checkEnd)
      }
    }, 10) // Check every 10ms for precise stopping

    // Clean up interval if audio is paused manually
    audioRef.current.addEventListener('pause', () => clearInterval(checkEnd), { once: true })
  }

  const handlePlayPause = () => {
    if (isPlaying) {
      pause()
    } else {
      play()
    }
  }

  const seekRelative = (seconds: number) => {
    if (!audioRef.current) return
    const newTime = Math.max(0, audioRef.current.currentTime + seconds)
    seek(newTime)
  }

  // Get edited or original timestamps for a card
  const getTimestamps = (card: any) => {
    const edited = editedTimestamps.get(card._id)
    if (edited) return edited

    return card.timestamp || { start: 0, end: 0 }
  }

  if (!flashcards) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="text-sky-blue font-mono">Loading flashcards...</div>
      </div>
    )
  }

  // Filter only flashcards with audio
  const flashcardsWithAudio = flashcards.filter(card => card.audioUrl && card.sourceSongId)

  return (
    <div className="max-w-7xl mx-auto p-6">
      <h1 className="text-2xl font-bold text-terminal-green font-mono mb-6">
        [TIMESTAMP EDITOR - MILLISECOND PRECISION]
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Flashcard List */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-sky-blue font-mono mb-3">
            [FLASHCARDS WITH AUDIO ({flashcardsWithAudio.length})]
          </h2>

          <div className="space-y-2 max-h-[70vh] overflow-y-auto">
            {flashcardsWithAudio.map((card) => {
              const timestamps = getTimestamps(card)
              const hasEdits = editedTimestamps.has(card._id)

              return (
                <div
                  key={card._id}
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                    selectedCard === card._id
                      ? 'border-terminal-green bg-terminal-green/10'
                      : 'border-sky-blue/30 hover:border-sky-blue/60'
                  }`}
                  onClick={() => setSelectedCard(card._id)}
                >
                  <div className="font-medium text-terminal-green font-mono">
                    {card.frontSpanish.join(' ')}
                  </div>
                  <div className="text-sm text-sky-blue font-mono mt-1">
                    {card.backEnglish.join(' ')}
                  </div>

                  {/* Timestamp Editor */}
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-trail-tan font-mono">Start (MM:SS.mmm)</label>
                      <input
                        type="text"
                        value={formatTimeMs(timestamps.start)}
                        onChange={(e) => {
                          e.stopPropagation()
                          handleStartTimeChange(card._id, e.target.value)
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full px-2 py-1 bg-black border border-trail-tan text-terminal-green font-mono text-sm rounded focus:border-terminal-green focus:outline-none"
                        placeholder="0:00.000"
                      />
                    </div>

                    <div>
                      <label className="text-xs text-trail-tan font-mono">End (MM:SS.mmm)</label>
                      <input
                        type="text"
                        value={formatTimeMs(timestamps.end)}
                        onChange={(e) => {
                          e.stopPropagation()
                          handleEndTimeChange(card._id, e.target.value)
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full px-2 py-1 bg-black border border-trail-tan text-terminal-green font-mono text-sm rounded focus:border-terminal-green focus:outline-none"
                        placeholder="0:00.000"
                      />
                    </div>
                  </div>

                  {/* Status Indicators */}
                  <div className="flex items-center gap-2 mt-2">
                    {hasEdits && (
                      <span className="text-xs text-yellow-500 font-mono animate-pulse">
                        [EDITED]
                      </span>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        const timestamps = getTimestamps(card)
                        playSegment(timestamps.start, timestamps.end)
                      }}
                      className="text-xs text-sky-blue font-mono hover:text-terminal-green"
                    >
                      🎵 Test Segment
                    </button>
                  </div>
                </div>
              )
            })}
          </div>

          {flashcardsWithAudio.length === 0 && (
            <div className="text-center py-8 border-2 border-yellow-500/50 bg-yellow-900/20 rounded-lg">
              <p className="text-yellow-400 font-mono">
                No flashcards with audio found
              </p>
            </div>
          )}
        </div>

        {/* Right: Audio Controls */}
        <div className="space-y-4">
          {currentCard && currentCard.audioUrl && (
            <>
              <h2 className="text-lg font-semibold text-sky-blue font-mono mb-3">
                [AUDIO CONTROLS]
              </h2>

              {/* Audio Player */}
              <div className="terminal-border p-4 bg-black">
                <audio
                  ref={audioRef}
                  src={currentCard.audioUrl}
                  preload="auto"
                  onLoadedMetadata={() => {
                    if (audioRef.current) {
                      audioRef.current.playbackRate = playbackSpeed
                    }
                  }}
                />

                {/* Current Time Display */}
                <div className="text-center text-2xl text-terminal-green font-mono mb-4">
                  {formatTimeMs(localCurrentTime)}
                </div>

                {/* Playback Controls */}
                <div className="flex items-center justify-center gap-4 mb-4">
                  <button
                    onClick={() => seekRelative(-0.1)}
                    className="px-3 py-2 bg-sky-blue/10 hover:bg-sky-blue/20 text-sky-blue border border-sky-blue font-mono text-sm rounded"
                  >
                    [-100ms]
                  </button>

                  <button
                    onClick={handlePlayPause}
                    className="px-4 py-2 bg-terminal-green/10 hover:bg-terminal-green/20 text-terminal-green border-2 border-terminal-green font-mono font-bold rounded flex items-center gap-2"
                  >
                    {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    {isPlaying ? '[PAUSE]' : '[PLAY]'}
                  </button>

                  <button
                    onClick={() => seekRelative(0.1)}
                    className="px-3 py-2 bg-sky-blue/10 hover:bg-sky-blue/20 text-sky-blue border border-sky-blue font-mono text-sm rounded"
                  >
                    [+100ms]
                  </button>
                </div>

                {/* Fine Controls */}
                <div className="grid grid-cols-4 gap-2 mb-4">
                  <button
                    onClick={() => seekRelative(-0.01)}
                    className="px-2 py-1 bg-trail-tan/10 hover:bg-trail-tan/20 text-trail-tan border border-trail-tan font-mono text-xs rounded"
                  >
                    -10ms
                  </button>
                  <button
                    onClick={() => seekRelative(-0.001)}
                    className="px-2 py-1 bg-trail-tan/10 hover:bg-trail-tan/20 text-trail-tan border border-trail-tan font-mono text-xs rounded"
                  >
                    -1ms
                  </button>
                  <button
                    onClick={() => seekRelative(0.001)}
                    className="px-2 py-1 bg-trail-tan/10 hover:bg-trail-tan/20 text-trail-tan border border-trail-tan font-mono text-xs rounded"
                  >
                    +1ms
                  </button>
                  <button
                    onClick={() => seekRelative(0.01)}
                    className="px-2 py-1 bg-trail-tan/10 hover:bg-trail-tan/20 text-trail-tan border border-trail-tan font-mono text-xs rounded"
                  >
                    +10ms
                  </button>
                </div>

                {/* Capture Buttons */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <button
                    onClick={() => captureCurrentTime('start')}
                    className="px-4 py-2 bg-sky-blue/10 hover:bg-sky-blue/20 text-sky-blue border border-sky-blue font-mono font-bold rounded"
                  >
                    [CAPTURE START]
                  </button>
                  <button
                    onClick={() => captureCurrentTime('end')}
                    className="px-4 py-2 bg-sky-blue/10 hover:bg-sky-blue/20 text-sky-blue border border-sky-blue font-mono font-bold rounded"
                  >
                    [CAPTURE END]
                  </button>
                </div>

                {/* Playback Speed */}
                <div className="flex items-center gap-3">
                  <label className="text-trail-tan font-mono text-sm">Speed:</label>
                  <select
                    value={playbackSpeed}
                    onChange={(e) => {
                      const speed = parseFloat(e.target.value)
                      setPlaybackSpeed(speed)
                      if (audioRef.current) {
                        audioRef.current.playbackRate = speed
                      }
                    }}
                    className="px-2 py-1 bg-black border border-trail-tan text-terminal-green font-mono text-sm rounded"
                  >
                    <option value="0.25">0.25x</option>
                    <option value="0.5">0.5x</option>
                    <option value="0.75">0.75x</option>
                    <option value="1.0">1.0x</option>
                    <option value="1.25">1.25x</option>
                    <option value="1.5">1.5x</option>
                  </select>
                </div>

                {/* Test Segment Button */}
                {selectedCard && editedTimestamps.has(selectedCard) && (
                  <button
                    onClick={() => {
                      const timestamps = getTimestamps({ _id: selectedCard })
                      playSegment(timestamps.start, timestamps.end)
                    }}
                    className="w-full mt-4 px-4 py-2 bg-terminal-green/10 hover:bg-terminal-green/20 text-terminal-green border-2 border-terminal-green font-mono font-bold rounded"
                  >
                    [TEST EDITED SEGMENT]
                  </button>
                )}
              </div>

              {/* Current Card Info */}
              <div className="terminal-border p-4 bg-black">
                <h3 className="text-sm font-semibold text-sky-blue font-mono mb-2">
                  [CURRENT FLASHCARD]
                </h3>
                <div className="space-y-2">
                  <div>
                    <span className="text-trail-tan font-mono text-xs">Spanish:</span>
                    <div className="text-terminal-green font-mono">{currentCard.frontSpanish.join(' ')}</div>
                  </div>
                  <div>
                    <span className="text-trail-tan font-mono text-xs">English:</span>
                    <div className="text-sky-blue font-mono">{currentCard.backEnglish.join(' ')}</div>
                  </div>
                  <div>
                    <span className="text-trail-tan font-mono text-xs">Current Timestamps:</span>
                    <div className="text-yellow-400 font-mono">
                      {formatTimeMs(getTimestamps(currentCard).start)} - {formatTimeMs(getTimestamps(currentCard).end)}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {!currentCard && (
            <div className="terminal-border p-8 bg-black text-center">
              <Volume2 className="w-12 h-12 text-sky-blue/50 mx-auto mb-4" />
              <p className="text-sky-blue/70 font-mono">
                Select a flashcard to edit timestamps
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Save Bar */}
      {editedTimestamps.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-black border-t-2 border-terminal-green p-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <span className="text-terminal-green font-mono">
              {editedTimestamps.size} flashcard{editedTimestamps.size !== 1 ? 's' : ''} edited
            </span>
            <div className="flex gap-3">
              <button
                onClick={() => setEditedTimestamps(new Map())}
                className="px-4 py-2 border-2 border-red-500 text-red-500 font-mono font-bold rounded hover:bg-red-500/10 flex items-center gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                [DISCARD CHANGES]
              </button>
              <button
                className="px-4 py-2 bg-terminal-green/10 hover:bg-terminal-green/20 text-terminal-green border-2 border-terminal-green font-mono font-bold rounded flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                [SAVE ALL CHANGES]
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}